import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Logo } from '../components/Logo';

// Public terms of service. Linked from the Register screen and the app stores.
const LAST_UPDATED = '1 July 2026';
const CONTACT_EMAIL = 'support@solaniq.ai';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
        {children}
      </div>
    </section>
  );
}

export function Terms() {
  return (
    <div className="min-h-screen bg-screen">
      <header className="border-b border-gray-200 dark:border-gray-700 bg-screen">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo className="h-8" />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Terms of Service</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Last updated: {LAST_UPDATED}
          </p>
        </div>

        <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
          These Terms of Service ("Terms") govern your use of the SolanIQ application and service
          ("SolanIQ", "we", "us"), operated by SolanIQ Pty Ltd, 14 Paringa Place, Bangor NSW 2234,
          Australia. By creating an account or using the service, you agree to these Terms. If you do
          not agree, do not use the service.
        </p>

        <Section title="1. The service">
          <p>
            SolanIQ optimises home solar battery systems against electricity prices and solar
            forecasts. It connects to your inverter and electricity retailer, generates optimisation
            plans, and — where you enable it — issues charge/discharge commands to your battery. The
            service depends on third-party data (prices, forecasts) and hardware that may at times be
            delayed, unavailable, or inaccurate.
          </p>
        </Section>

        <Section title="2. Eligibility and accounts">
          <p>
            You must be at least 18 years old and able to enter a binding contract. You are
            responsible for the accuracy of the information you provide, for keeping your login
            credentials secure, and for all activity under your account. Notify us promptly of any
            unauthorised use.
          </p>
        </Section>

        <Section title="3. Subscriptions, trials and billing">
          <p>
            SolanIQ is offered on a subscription basis, and new accounts may include a free trial.
            Payments are processed by Stripe. Unless cancelled, subscriptions renew automatically for
            successive periods at the then-current price, and the payment method on file is charged.
            You can cancel at any time, effective at the end of the current billing period; except
            where required by law, payments already made are non-refundable. We may change pricing on
            reasonable notice.
          </p>
        </Section>

        <Section title="4. Battery control and your responsibility">
          <p>
            When you enable automated control, you authorise SolanIQ to send charge, discharge, and
            mode commands to your battery system on your behalf. You remain responsible for your
            hardware, its installation, warranty conditions, and safe operation, and for ensuring
            that automated control is permitted for your system, retailer plan, and network.
          </p>
          <p>
            SolanIQ provides optimisation and control as a decision-support tool. Financial outcomes
            depend on your tariff, hardware, solar, consumption, and market conditions, and{' '}
            <strong>savings are not guaranteed</strong>. You should monitor your system and are free
            to use "Suggest" mode or disable automated control at any time.
          </p>
        </Section>

        <Section title="5. Acceptable use">
          <p>
            You agree not to misuse the service: no reverse engineering, interfering with or
            disrupting the service, attempting unauthorised access, or using it unlawfully or in a way
            that infringes others' rights.
          </p>
        </Section>

        <Section title="6. Third-party services">
          <p>
            The service integrates with third parties such as your electricity retailer, hardware
            vendors, and payment and infrastructure providers. Your use of those services is subject
            to their own terms, and we are not responsible for their acts, omissions, or availability.
          </p>
        </Section>

        <Section title="7. Intellectual property">
          <p>
            SolanIQ and its software, content, and trademarks are owned by SolanIQ Pty Ltd. We grant
            you a limited, non-exclusive, non-transferable licence to use the app for its intended
            purpose while these Terms are in effect. You retain your own data.
          </p>
        </Section>

        <Section title="8. Disclaimers">
          <p>
            To the extent permitted by law, the service is provided "as is" and "as available"
            without warranties of any kind. We do not warrant that it will be uninterrupted,
            error-free, or that it will achieve any particular energy or financial result. Nothing in
            these Terms excludes rights you have under the Australian Consumer Law that cannot lawfully
            be excluded.
          </p>
        </Section>

        <Section title="9. Limitation of liability">
          <p>
            To the maximum extent permitted by law, SolanIQ Pty Ltd is not liable for any indirect,
            incidental, special, or consequential loss, or for lost savings, revenue, or data. Where
            our liability cannot be excluded, it is limited to resupplying the service or refunding
            the fees you paid for the service in the three months before the event giving rise to the
            claim.
          </p>
        </Section>

        <Section title="10. Termination">
          <p>
            You may stop using the service and delete your account at any time from within the app. We
            may suspend or terminate access if you breach these Terms or where necessary to protect
            the service or other users. Terms that by their nature should survive termination will do
            so.
          </p>
        </Section>

        <Section title="11. Changes">
          <p>
            We may update the service or these Terms from time to time. Material changes will be
            notified in the app or by email, and the "Last updated" date above will change. Continued
            use after changes take effect constitutes acceptance.
          </p>
        </Section>

        <Section title="12. Governing law">
          <p>
            These Terms are governed by the laws of New South Wales, Australia, and you submit to the
            non-exclusive jurisdiction of its courts.
          </p>
        </Section>

        <Section title="13. Contact">
          <p>
            <a className="text-brand-orange hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>{' '}
            — SolanIQ Pty Ltd, 14 Paringa Place, Bangor NSW 2234, Australia
          </p>
        </Section>
      </main>
    </div>
  );
}
